import { EquirectangularReflectionMapping, PMREMGenerator } from 'three';

import type { TextureManager } from '../texture-manager.class';

export interface SetEnvironmentTextureParams {
	key: string;
}

/**
 * Update environment texture for reflections
 */
export const setEnvironmentTextureCfn = (self: TextureManager, params: SetEnvironmentTextureParams): boolean => {
	const { key } = params;
	const config = self.config;
	const textures = self.textures;
	const materials = self.materials;

	const texture = textures.get(key);
	if (!texture) {
		console.warn('[setEnvironmentTexture] Environment texture not found:', key);
		return false;
	}

	if (!config.renderer) {
		console.warn('TextureManager: No renderer available for PMREMGenerator');
		const envTexture = texture.clone();
		envTexture.mapping = EquirectangularReflectionMapping;
		envTexture.needsUpdate = true;
		self.environmentMap = envTexture;
		return true;
	}

	// Save reference to old env map for deferred disposal
	const previousEnvMap = self.environmentMap;

	const pmremGenerator = new PMREMGenerator(config.renderer);
	pmremGenerator.compileEquirectangularShader();

	try {
		texture.mapping = EquirectangularReflectionMapping;
		const envMap = pmremGenerator.fromEquirectangular(texture).texture;
		self.environmentMap = envMap;
	} catch (e) {
		console.error('Error generating PMREM environment map:', e);
		const envTexture = texture.clone();
		envTexture.mapping = EquirectangularReflectionMapping;
		envTexture.needsUpdate = true;
		self.environmentMap = envTexture;
	}

	pmremGenerator.dispose();

	// Update all materials with the new environment map
	const newEnvMap = self.environmentMap;
	for (const material of materials.values()) {
		material.envMap = newEnvMap;
		material.needsUpdate = true;
	}

	// Dispose previous PMREM envMap AFTER the new one is set on all materials.
	// Deferred to next frame to avoid GL errors from the renderer referencing
	// the disposed texture (scene.background may still point to it until the
	// caller updates it).
	if (previousEnvMap && previousEnvMap !== newEnvMap && (previousEnvMap as any).isRenderTargetTexture) {
		requestAnimationFrame(() => {
			try {
				previousEnvMap.dispose();
			} catch (e) {
				console.warn('Error disposing previous PMREM envMap:', e);
			}
		});
	}

	return true;
};
